/* Shared Scrub alternate view. Native Shopify selects, variant requests, media and cart remain authoritative. */
(() => {
  if (window.DwanScrubShared) return;
  const VIEW = 'scrub';
  const LIMIT = 250;
  const parse = (node, fallback = null) => { try { return JSON.parse(node?.textContent || 'null') ?? fallback; } catch { return fallback; } };
  const asCount = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const context = new URL(window.location.href);
  const previewThemeId = context.searchParams.get('preview_theme_id');
  const withViewContext = (input, params = {}, includeView = true) => {
    const url = new URL(input, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Scrub product URLs must be same-origin.');
    if (includeView) url.searchParams.set('view', VIEW);
    if (previewThemeId && /^\d+$/.test(previewThemeId)) url.searchParams.set('preview_theme_id', previewThemeId);
    for (const [key, value] of Object.entries(params)) {
      if (value == null || value === '') url.searchParams.delete(key);
      else url.searchParams.set(key, String(value));
    }
    return `${url.pathname}${url.search}${url.hash}`;
  };
  const optionValueById = (native, index, id) => {
    const values = native?.option_values?.[index]?.values;
    return Array.isArray(values) ? values.find((value) => value && String(value.id) === String(id)) || null : null;
  };
  const selectedCanonicalValues = (picker, native) => {
    if (!picker || !Array.isArray(native?.option_names)) return null;
    const groups = Array.from(picker.querySelectorAll('.product-form__input'));
    if (groups.length !== native.option_names.length) return null;
    const values = groups.map((group, index) => {
      const select = group.querySelector('select');
      const selected = select ? Array.from(select.selectedOptions || []) : Array.from(group.querySelectorAll('input[type="radio"]')).filter((node) => node.checked);
      if (selected.length !== 1) return null;
      return optionValueById(native, index, selected[0].dataset.optionValueId)?.value;
    });
    return values.every((value) => typeof value === 'string') ? values : null;
  };
  const registryComplete = (native) => {
    if (!native || !Array.isArray(native.variants) || !Array.isArray(native.option_names)) return false;
    const count = native.variants.length;
    // Deliberately conservative. This implementation is not an arbitrary high-variant selector.
    if (!count || count >= LIMIT) return false;
    const totals = [native.reported_count, native.visible_count, native.emitted_count, native.total_from_pagination].map(asCount);
    if (totals.some((total) => total !== count)) return false;
    if (!native.option_names.length || new Set(native.option_names).size !== native.option_names.length) return false;
    if (!Array.isArray(native.option_values) || native.option_values.length !== native.option_names.length) return false;
    const optionIds = new Set();
    for (const [index, group] of native.option_values.entries()) {
      if (group?.name !== native.option_names[index] || group.position !== index + 1 || !Array.isArray(group.values) || !group.values.length) return false;
      const values = new Set();
      for (const value of group.values) {
        const id = String(value?.id);
        if (!/^\d+$/.test(id) || optionIds.has(id) || typeof value.value !== 'string' || values.has(value.value)) return false;
        optionIds.add(id); values.add(value.value);
      }
    }
    const ids = new Set(); const tuples = new Set();
    for (const variant of native.variants) {
      if (!variant || !/^\d+$/.test(String(variant.id)) || ids.has(String(variant.id)) || typeof variant.available !== 'boolean') return false;
      if (!Array.isArray(variant.options) || variant.options.length !== native.option_names.length || variant.options.some((value, index) => typeof value !== 'string' || !native.option_values[index].values.some((entry) => entry.value === value))) return false;
      const tuple = JSON.stringify(variant.options);
      if (tuples.has(tuple)) return false;
      ids.add(String(variant.id)); tuples.add(tuple);
    }
    return true;
  };
  const resolveRole = (role, names) => {
    if (!role || !Array.isArray(names)) return -1;
    const hasName = typeof role.name === 'string' && role.name.length > 0;
    const byName = hasName ? names.indexOf(role.name) : -1;
    const hasPosition = role.position != null && role.position !== '';
    const byPosition = hasPosition && Number.isInteger(Number(role.position)) ? Number(role.position) - 1 : -1;
    if (hasName && (byName < 0 || (hasPosition && byName !== byPosition))) return -1;
    const index = hasName ? byName : byPosition;
    return index >= 0 && index < names.length ? index : -1;
  };
  const roles = (profile, native) => {
    const names = native?.option_names;
    const model = resolveRole(profile?.model_option, names);
    const coverage = resolveRole(profile?.coverage_option, names);
    const hasColor = profile?.color_option != null;
    const color = hasColor ? resolveRole(profile.color_option, names) : -1;
    const claimed = [model, coverage, ...(hasColor ? [color] : [])];
    const valid = Array.isArray(names) && names.length === claimed.length &&
      claimed.every((index) => index >= 0) && new Set(claimed).size === claimed.length &&
      (!hasColor || (names[color] === 'Color' && native.option_values?.[color]?.values?.length === 1 &&
        typeof profile.color_option.value === 'string' &&
        native.option_values[color].values[0].value === profile.color_option.value));
    return { model, coverage, color, valid };
  };
  const profileSupported = (profile, native) => profile?.schema_version === 1 && profile?.series_key === VIEW &&
    ['macbook', 'asus', 'dell', 'microsoft', 'lenovo'].includes(profile.brand_key) &&
    typeof profile.brand_label === 'string' && profile.brand_label.length > 0 && roles(profile, native).valid;
  const duoModel = (profile, model) => profile?.schema_version === 1 && profile?.series_key === VIEW && profile?.brand_key === 'asus' && profile?.brand_label === 'ASUS' && model === 'ASUS Zenbook DUO (2026) UX8407AA';
  const duoCopy = () => parse(document.querySelector('[data-hb-duo-copy]'), {});
  const surfaceLabel = (profile, code, model) => duoModel(profile, model) && code === 'B' ? duoCopy().keyboardBack || profile.surfaces?.[code]?.label : profile.surfaces?.[code]?.label;
  const coverageLabel = (profile, coverage, model) => duoModel(profile, model) && coverage === 'Top Inside Bottom & Screen edge' ? duoCopy().coverage || coverage : coverage;
  const packageDefinition = (profile, coverage) => {
    const definition = Object.prototype.hasOwnProperty.call(profile?.coverages || {}, coverage) ? profile.coverages[coverage] : null;
    if (!definition || !Array.isArray(definition.included) || !definition.included.length || new Set(definition.included).size !== definition.included.length) return null;
    if (definition.included.some((code) => !Object.prototype.hasOwnProperty.call(profile.surfaces || {}, code) || !profile.surfaces[code]?.label || !profile.surfaces[code]?.material || !profile.materials?.[profile.surfaces[code].material]?.label)) return null;
    return definition;
  };
  const matchingVariant = (source, selected, target) => {
    if (!registryComplete(source?.native) || !registryComplete(target?.native)) return null;
    if (!profileSupported(source.profile, source.native) || !profileSupported(target.profile, target.native)) return null;
    if (source.profile.brand_key !== target.profile.brand_key || source.profile.brand_label !== target.profile.brand_label || source.profile.series_key !== target.profile.series_key) return null;
    const a = roles(source.profile, source.native); const b = roles(target.profile, target.native);
    if (!selected || !Array.isArray(selected.options) || selected.options.length !== source.native.option_names.length) return null;
    const registered = source.native.variants.find((variant) => String(variant.id) === String(selected.id));
    if (!registered || registered.options.some((value, index) => value !== selected.options[index])) return null;
    if (a.color >= 0 && selected.options[a.color] !== source.profile.color_option.value) return null;
    // Color belongs to the destination product. The audited adapter alone permits
    // the recognized singleton Color axis to be added/dropped between siblings.
    const wanted = target.native.option_names.map((name, index) => index === b.model ? selected.options[a.model] :
      index === b.coverage ? selected.options[a.coverage] : index === b.color ? target.profile.color_option.value : undefined);
    if (wanted.some((value) => typeof value !== 'string')) return null;
    return target.native.variants.find((variant) => variant.options.every((value, index) => value === wanted[index])) || null;
  };
  const materialRows = (profile, coverage, model) => {
    const definition = packageDefinition(profile, coverage);
    if (!definition) return [];
    return Object.entries(profile.materials || {}).map(([key, material]) => ({
      label: material.label,
      parts: definition.included.filter((code) => profile.surfaces[code].material === key).map((code) => `${code} · ${surfaceLabel(profile, code, model)}`).join(' + '),
    })).filter((row) => row.parts && row.label);
  };
  const renderMaterials = (node, profile, coverage, model) => {
    if (!node) return;
    const rows = materialRows(profile, coverage, model);
    node.replaceChildren(...rows.map((row) => {
      const p = document.createElement('p'); p.dataset.hbMaterialRow = '';
      const strong = document.createElement('strong'); strong.textContent = row.parts;
      p.append(strong, document.createElement('br'), document.createTextNode(row.label));
      return p;
    }));
    const note = packageDefinition(profile, coverage)?.note;
    if (note) { const p = document.createElement('p'); p.className = 'hb-details__note'; p.textContent = note; node.append(p); }
  };
  const trackedDocuments = new WeakMap();


  function interactionState(document) {
    let state = trackedDocuments.get(document);
    if (state) return state;
    state = { keyboard: false, focusVersion: 0, pointerVersion: 0, blurVersion: 0 };
    document.addEventListener('keydown', (event) => {
      if (event.isTrusted && !['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) state.keyboard = true;
    }, true);
    document.addEventListener('pointerdown', (event) => {
      if (!event.isTrusted) return;
      state.keyboard = false; state.pointerVersion += 1;
    }, { capture: true, passive: true });
    document.addEventListener('focusin', () => { state.focusVersion += 1; }, true);
    document.defaultView.addEventListener('blur', () => { state.blurVersion += 1; });
    trackedDocuments.set(document, state);
    return state;
  }

  function installScrubKeyboardFocusRepair(info, {
    fetch = globalThis.fetch,
    DOMParser = globalThis.DOMParser,
    AbortController = globalThis.AbortController,
    console = globalThis.console,
  } = {}) {
    const document = info.ownerDocument;
    if (!info.hasAttribute('data-sc-shared-view') || info.dataset.scKeyboardFocus !== 'true' || info.hbKeyboardFocusRepaired) return false;
    info.hbKeyboardFocusRepaired = true;
    const state = interactionState(document);
    const nativeRender = info.renderProductInfo;
    info.renderProductInfo = function ({ requestUrl, targetId, callback }) {
      const option = document.getElementById(targetId);
      const source = option?.tagName === 'OPTION' ? option.closest('select') : null;
      if (option && this.variantSelectors?.contains(source || option) && (!option.dataset.productUrl || option.dataset.productUrl === this.dataset.url)) {
        // Preserve native radio fallbacks without reusing an earlier verified response.
        this.hbSelectionState ||= { responseVerified: false };
        this.hbSelectionState.responseVerified = false;
      }
      if (!source?.id || !this.variantSelectors?.contains(source) ||
          (option.dataset.productUrl && option.dataset.productUrl !== this.dataset.url)) {
        return nativeRender.call(this, { requestUrl, targetId, callback });
      }
      const id = source.id;
      const focusVersion = state.focusVersion;
      const pointerVersion = state.pointerVersion;
      const blurVersion = state.blurVersion;
      const keyboardOwner = state.keyboard && document.activeElement === source && document.hasFocus();
      this.abortController?.abort();
      const controller = this.abortController = new AbortController();
      const isCurrent = () => this.isConnected && !controller.signal.aborted && this.abortController === controller;
      return fetch(requestUrl, { signal: controller.signal })
        .then((response) => response.text())
        .then((responseText) => {
          if (!isCurrent()) return;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const restore = keyboardOwner && state.keyboard && document.hasFocus() &&
            document.activeElement === source && state.focusVersion === focusVersion &&
            state.pointerVersion === pointerVersion && state.blurVersion === blurVersion;
          this.pendingRequestUrl = null;
          callback(html);
          if (!restore || !isCurrent() || !document.hasFocus() || state.focusVersion !== focusVersion ||
              state.pointerVersion !== pointerVersion || state.blurVersion !== blurVersion ||
              (document.activeElement !== document.body && document.activeElement !== source)) return;
          const replacement = document.getElementById(id);
          if (replacement?.tagName !== 'SELECT' || replacement.disabled ||
              !this.variantSelectors?.contains(replacement) || !replacement.getClientRects().length) return;
          replacement.focus({ preventScroll: true });
        })
        .catch((error) => {
          if (this.abortController !== controller) return;
          if (error.name === 'AbortError') console.log('Fetch aborted by user');
          else console.error(error);
          this.variantSelectors?.rejectPendingSelectPromise(error);
        });
    };
    return true;
  }
  const installNativeInfo = (info) => {
    if (!info?.hasAttribute('data-sc-shared-view')) return;
    installScrubKeyboardFocusRepair(info);
    info.querySelectorAll('script[data-hb-profile], script[data-hb-native], script[data-hb-sibling], variant-selects script[data-selected-variant]').forEach((node) => node.setAttribute('translate', 'no'));
    // Apply the mobile default once per product-info, never after a shopper opens it.
    if (!info.hbProductDetailsDefaulted) {
      const details = info.querySelector('[data-hb-product-details]');
      if (details) {
        info.hbProductDetailsDefaulted = true;
        if (window.matchMedia?.('(max-width: 749px)')?.matches) details.removeAttribute('open');
      }
    }
    if (info.hbSharedPatched) return;
    // Methods are replaced only on this view's element. All native variant, price,
    // media, quantity and cart update implementations are otherwise untouched.
    info.hbSharedPatched = true;
    const nativeUpdateProductInfo = info.handleUpdateProductInfo;
    if (typeof nativeUpdateProductInfo === 'function') {
      info.handleUpdateProductInfo = function (productUrl) {
        const applyNativeResponse = nativeUpdateProductInfo.call(this, productUrl);
        return (html) => {
          const variant = this.getSelectedVariant(html);
          applyNativeResponse(html);
          // Dawn returns before publishing variantChange for a nonexistent tuple.
          // Settle that real response too; keep purchasing locked, never substitute a model.
          if (!variant) this.querySelector('dwan-sc-shared-controls')?.acceptNativeResponse?.(html, null);
        };
      };
    }
    info.buildRequestUrlWithParams = function (url, optionValues = [], fullPage = false) {
      return withViewContext(url, { section_id: fullPage ? null : this.sectionId, option_values: optionValues.length ? optionValues.join(',') : null, variant: null });
    };
    info.updateURL = function (url, variantId) {
      const next = withViewContext(url, { variant: variantId || null, option_values: null, section_id: null });
      this.querySelector('share-button')?.updateUrl(`${window.location.origin}${next}`);
      if (this.dataset.updateUrl !== 'false') window.history.replaceState({}, '', next);
    };
    info.fetchQuantityRules = function () {
      const id = this.productForm?.variantIdInput?.value;
      if (!id) return;
      const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
      spinner?.classList.remove('hidden');
      return fetch(withViewContext(this.dataset.url, { variant: id, section_id: this.dataset.section, option_values: null }))
        .then((response) => { if (!response.ok) throw new Error('Could not refresh quantity rules.'); return response.text(); })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          if (html.getElementById(`Quantity-Form-${this.dataset.section}`)) this.updateQuantityRules(this.dataset.section, html);
        })
        .catch((error) => console.error(error))
        .finally(() => spinner?.classList.add('hidden'));
    };
    // Native connectedCallback already bound the original function. Replace that
    // subscription, rather than leaving a callback that would lose view=scrub.
    if (info.quantityInput && info.quantityForm && !info.dataset.originalSection && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      info.cartUpdateUnsubscriber?.();
      info.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, info.fetchQuantityRules.bind(info));
    }
  };
  const embedUrl = (input) => {
    try {
      const url = new URL(input); if (url.protocol !== 'https:') return null;
      let id = '';
      if (['www.youtube.com', 'youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname)) id = url.pathname.startsWith('/embed/') ? url.pathname.split('/')[2] : url.searchParams.get('v');
      if (url.hostname === 'youtu.be') id = url.pathname.slice(1);
      if (id && /^[A-Za-z0-9_-]{11}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}?rel=0`;
      if (['vimeo.com', 'www.vimeo.com'].includes(url.hostname) && /^\/\d+$/.test(url.pathname)) return `https://player.vimeo.com/video/${url.pathname.slice(1)}`;
    } catch { /* Invalid configuration is omitted. */ }
    return null;
  };
  const api = { withViewContext, optionValueById, selectedCanonicalValues, registryComplete, resolveRole, roles, profileSupported, packageDefinition, matchingVariant, materialRows, installNativeInfo, embedUrl };
  window.DwanScrubShared = api;
  document.addEventListener('product-info:loaded', (event) => installNativeInfo(event.target));
  customElements.whenDefined('product-info').then(() => document.querySelectorAll('product-info[data-sc-shared-view]').forEach(installNativeInfo));

  if (!customElements.get('dwan-sc-shared-controls')) customElements.define('dwan-sc-shared-controls', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.info = this.closest('product-info'); if (!this.info) return;
      this.initialized = true; this.selectionPending = Boolean(this.info.pendingRequestUrl); this.lastSignature = null;
      installNativeInfo(this.info);
      this.profile = parse(this.querySelector('[data-hb-profile]'), {});
      this.native = parse(this.querySelector('[data-hb-native]'), {});
      this.source = { profile: this.profile, native: this.native };
      this.siblings = new Map(Array.from(this.querySelectorAll('[data-hb-sibling]')).map((node) => [node.dataset.hbSibling, parse(node, {})]));
      this.integrity = registryComplete(this.native) && profileSupported(this.profile, this.native);
      this.setupSelectionFreshness();
      this.onClick = (event) => { const link = event.target.closest('[data-hb-handle]'); if (link?.getAttribute('aria-disabled') === 'true') event.preventDefault(); };
      this.addEventListener('click', this.onClick);
      this.setupThumbnailGrid();
      this.checkPurchaseGuard(false); this.update(this.readVariant(), false);
      this.pickerObserver = new MutationObserver(() => { this.update(this.readVariant(), false); });
      this.pickerObserver.observe(this.info, { childList: true, subtree: true });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeSelection = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (!this.info.contains(data?.event?.target)) return;
          if (this.selectionState) this.selectionState.responseVerified = false;
          this.selectionPending = true;
          this.update(null, false);
        });
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== String(this.info.dataset.originalSection || this.info.dataset.section)) return;
          this.acceptNativeResponse(data.html, data.variant);
        });
      }
      this.decorateRecommendations();
      this.recommendationObserver = new MutationObserver(() => this.decorateRecommendations());
      document.querySelectorAll('.dwan-sc-shared-finishes').forEach((node) => this.recommendationObserver.observe(node, {subtree: true, attributes: true, attributeFilter: ['href']}));
    }
    disconnectedCallback() {
      this.unsubscribe?.(); this.unsubscribeSelection?.(); this.thumbnailObserver?.disconnect(); this.pickerObserver?.disconnect(); this.recommendationObserver?.disconnect();
      window.cancelAnimationFrame(this.thumbnailFrame); this.thumbnailFrame = undefined; this.removeEventListener('click', this.onClick); this.initialized = false;
      if (this.info?.dataset.productId) document.querySelectorAll(`dwan-sc-shared-details[data-product-id="${this.info.dataset.productId}"]`).forEach((details) => details.update?.(null, false));
    }
    readVariant() {
      const picker = this.info?.querySelector('variant-selects');
      const variant = parse(picker?.querySelector('[data-selected-variant]'));
      if (!variant || !Array.isArray(variant.options) || !Array.isArray(this.native?.option_names) || !Array.isArray(this.native?.variants)) return null;
      const values = selectedCanonicalValues(picker, this.native);
      if (!values || variant.options.length !== values.length) return null;
      const registered = this.native.variants.find((item) => String(item.id) === String(variant.id));
      if (!Array.isArray(registered?.options) || registered.options.length !== values.length || values.some((value, index) => typeof value !== 'string' || value !== variant.options[index] || value !== registered.options[index])) return null;
      const cartVariantId = this.info.querySelector('product-form input[name="id"]')?.value;
      if (String(cartVariantId || '') !== String(variant.id)) return null;
      return variant;
    }
    acceptNativeResponse(html, variant) {
      const fresh = html?.querySelector('product-info[data-sc-shared-view] dwan-sc-shared-controls');
      this.selectionPending = false;
      if (fresh) {
        this.profile = parse(fresh.querySelector('[data-hb-profile]'), {});
        this.native = parse(fresh.querySelector('[data-hb-native]'), {});
        this.source = { profile: this.profile, native: this.native };
        this.siblings = new Map(Array.from(fresh.querySelectorAll('[data-hb-sibling]')).map((node) => [node.dataset.hbSibling, parse(node, {})]));
        this.integrity = registryComplete(this.native) && profileSupported(this.profile, this.native);
      } else {
        this.integrity = false; this.profile = {}; this.native = {};
        this.source = { profile: this.profile, native: this.native }; this.siblings = new Map();
      }
      this.rememberSelectionSnapshot();
      this.verifySelectionResponse(Boolean(fresh), variant);
      this.update(variant, true);
    }
    readSelectionValues() {
      if (!this.integrity || this.selectionPending) return null;
      return selectedCanonicalValues(this.info?.querySelector('variant-selects'), this.native);
    }
    syncCoverageOptions(values) {
      if (!values || !this.integrity) return;
      const role = roles(this.profile, this.native); if (!role.valid) return;
      const picker = this.info?.querySelector('variant-selects');
      const group = Array.from(picker?.querySelectorAll('.product-form__input') || [])[role.coverage];
      const select = group?.querySelector('select');
      for (const option of Array.from(select?.options || [])) {
        const target = optionValueById(this.native, role.coverage, option.dataset.optionValueId);
        const exists = target && this.native.variants.some((variant) => variant.options.every((value, index) => value === (index === role.coverage ? target.value : values[index])));
        option.disabled = !exists;
      }
    }
    setupSelectionFreshness() {
      if (this.info.dataset.scKeyboardFocus !== 'true') return;
      // Preserve fail-closed native-response freshness, without any extra shopper confirmation.
      this.info.hbSelectionState ||= { responseVerified: this.integrity };
      this.selectionState = this.info.hbSelectionState;
      if (this.selectionState.snapshot) {
        const snapshot = this.selectionState.snapshot;
        this.profile = snapshot.profile; this.native = snapshot.native;
        this.siblings = new Map(snapshot.siblings);
        this.source = { profile: this.profile, native: this.native };
        this.integrity = registryComplete(this.native) && profileSupported(this.profile, this.native);
      } else this.rememberSelectionSnapshot();
    }
    rememberSelectionSnapshot() {
      if (this.selectionState) this.selectionState.snapshot = { profile: this.profile, native: this.native, siblings: Array.from(this.siblings) };
    }
    hasVerifiedNativeResponse() { return !this.selectionState || this.selectionState.responseVerified === true; }
    verifySelectionResponse(hasFreshControls, variant) {
      if (!this.selectionState) return;
      const current = hasFreshControls && this.integrity && !this.selectionPending ? this.readVariant() : null;
      this.selectionState.responseVerified = Boolean(current && String(current.id) === String(variant?.id));
    }
    checkPurchaseGuard(selectionValid = false) {
      const guard = this.info.querySelector('[data-hb-purchase-guard]'); if (!guard) return;
      const allowed = this.integrity && !this.selectionPending && selectionValid && this.hasVerifiedNativeResponse();
      guard.disabled = !allowed; guard.inert = !allowed; guard.classList.toggle('hb-purchase-pending', !allowed); guard.setAttribute('aria-busy', String(Boolean(this.selectionPending)));
      const status = this.info.querySelector('[data-hb-integrity-status]');
      if (status) { status.hidden = allowed; status.textContent = allowed ? '' : this.selectionPending ? 'Updating your selected model and coverage…' : !this.integrity ? 'The complete Scrub configuration could not be verified. Please reselect your model and coverage or contact us before ordering.' : 'Choose an available model and coverage before ordering.'; }
    }
    setupThumbnailGrid() {
      const gallery = this.info.querySelector('media-gallery'); if (!gallery) return;
      this.thumbnailFrame = undefined;
      this.queueThumbnailGrid = () => {
        if (this.thumbnailFrame !== undefined) return;
        this.thumbnailFrame = window.requestAnimationFrame(() => {
          this.thumbnailFrame = undefined;
          this.syncThumbnailGrid();
        });
      };
      this.thumbnailObserver = new MutationObserver((records) => {
        if (records.some(({ target }) => !target.closest?.('dwan-gallery-position'))) this.queueThumbnailGrid();
      });
      this.thumbnailObserver.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
      this.queueThumbnailGrid();
    }
    syncThumbnailGrid() {
      const list = this.info.querySelector('.thumbnail-list'); if (!list) return;
      const visible = Array.from(list.querySelectorAll('.thumbnail-list__item')).filter((item) => !item.hidden && window.getComputedStyle(item).display !== 'none');
      const columns = String(Math.max(1, visible.length <= 5 ? visible.length : Math.ceil(visible.length / 2)));
      if (list.style.getPropertyValue('--hb-thumbnail-columns') !== columns) list.style.setProperty('--hb-thumbnail-columns', columns);
      const count = String(visible.length);
      if (list.dataset.hbThumbnailCount !== count) list.dataset.hbThumbnailCount = count;
    }
    update(variant, announce) {
      this.queueThumbnailGrid?.();
      this.info?.querySelector('variant-selects [data-selected-variant]')?.setAttribute('translate', 'no');
      const current = this.integrity ? this.readVariant() : null;
      const selected = this.integrity && !this.selectionPending && this.hasVerifiedNativeResponse() && current && String(current.id) === String(variant?.id) ? this.native.variants.find((item) => String(item.id) === String(current.id)) : null;
      const selectionValues = this.readSelectionValues();
      this.syncCoverageOptions(selectionValues);
      // Avoid mutation-observer loops when our own summary text changes.
      const signature = `${selected?.id || 'none'}:${this.integrity}:${Boolean(this.selectionPending)}:${this.hasVerifiedNativeResponse()}:${JSON.stringify(selectionValues)}`;
      if (signature === this.lastSignature && !announce) return;
      this.lastSignature = signature; this.selected = selected;
      this.checkPurchaseGuard(Boolean(selected));
      let missing = false;
      this.querySelectorAll('[data-hb-handle]').forEach((link) => {
        const target = this.siblings.get(link.dataset.hbHandle);
        const match = this.integrity ? matchingVariant(this.source, selected, target) : null;
        const available = Boolean(match?.available);
        link.setAttribute('aria-disabled', String(!available));
        if (available) link.href = withViewContext(link.dataset.hbUrl, { variant: match.id });
        else { link.removeAttribute('href'); missing = true; }
        link.title = `${target?.profile?.colour_label || link.title.split(' — ')[0]}${available ? '' : ' — unavailable for this selection'}`;
      });
      const status = this.querySelector('[data-hb-colour-status]');
      if (status) { status.hidden = !missing; status.textContent = missing ? 'Some colors are unavailable for this exact selection.' : ''; }
      const role = roles(this.profile, this.native);
      const coverage = role.valid ? selected?.options[role.coverage] : null;
      const model = role.valid ? selected?.options[role.model] : null;
      this.info.querySelectorAll('[data-hb-duo-note]').forEach((node) => { node.hidden = !duoModel(this.profile, model) || this.selectionPending; });
      const coverageGroup = this.info.querySelectorAll('variant-selects .product-form__input')[role.coverage];
      coverageGroup?.querySelectorAll('option').forEach((option) => {
        const canonical = optionValueById(this.native, role.coverage, option.dataset.optionValueId)?.value;
        if (canonical !== 'Top Inside Bottom & Screen edge') return;
        if (duoModel(this.profile, model)) {
          if (!Object.prototype.hasOwnProperty.call(option.dataset, 'hbDuoOriginalLabel')) option.dataset.hbDuoOriginalLabel = option.getAttribute('label') || '';
          option.label = coverageLabel(this.profile, canonical, model);
        } else if (Object.prototype.hasOwnProperty.call(option.dataset, 'hbDuoOriginalLabel')) {
          const original = option.dataset.hbDuoOriginalLabel;
          if (original) option.setAttribute('label', original); else option.removeAttribute('label');
          delete option.dataset.hbDuoOriginalLabel;
        }
      });
      const definition = this.integrity && !this.selectionPending && coverage ? packageDefinition(this.profile, coverage) : null;
      const inclusion = this.info.querySelector('[data-hb-inclusion]');
      if (inclusion) {
        inclusion.hidden = !definition;
        if (definition) {
          const label = inclusion.querySelector('[data-hb-coverage-label]'); if (label) label.textContent = definition.code || coverage;
          const codes = Array.isArray(this.profile.surface_order) ? this.profile.surface_order : Object.keys(this.profile.surfaces || {});
          const pieces = codes.filter((code) => this.profile.surfaces?.[code]?.label).map((code) => {
            const li = document.createElement('li'); const included = definition.included.includes(code);
            li.dataset.hbPiece = code; li.dataset.included = String(included);
            const state = document.createElement('span'); state.className = 'visually-hidden'; state.textContent = included ? 'Included: ' : 'Not included: ';
            const icon = document.createElement('span'); icon.className = 'hb-piece-icon'; icon.setAttribute('aria-hidden', 'true'); icon.textContent = '✓';
            const text = document.createElement('span'); text.textContent = `${surfaceLabel(this.profile, code, model)} ${code}`;
            li.append(state, icon, text); return li;
          });
          inclusion.querySelector('[data-hb-pieces]')?.replaceChildren(...pieces);
          renderMaterials(inclusion.querySelector('[data-hb-selected-materials]'), this.profile, coverage, model);
          if (announce) { const live = inclusion.querySelector('[data-hb-selection-status]'); if (live) live.textContent = `${selected.options[role.model]}. ${coverageLabel(this.profile, coverage, model)}. Included pieces updated.`; }
        }
      }
      document.querySelectorAll(`dwan-sc-shared-details[data-product-id="${this.info.dataset.productId}"]`).forEach((details) => details.update?.(selected, announce));
    }
    decorateRecommendations() {
      const handles = new Set(Array.isArray(this.profile.colour_handles) ? this.profile.colour_handles : []);
      document.querySelectorAll('.dwan-sc-shared-finishes a[href]').forEach((link) => {
        try {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
          const handle = url.pathname.split('/products/')[1]?.split('/')[0];
          const next = withViewContext(url.href, {view: handles.has(handle) ? VIEW : null}, handles.has(handle));
          if (link.getAttribute('href') !== next) link.setAttribute('href', next);
        } catch { /* Leave unrelated native links unchanged. */ }
      });
    }
  });

  if (!customElements.get('dwan-sc-shared-details')) customElements.define('dwan-sc-shared-details', class extends HTMLElement {
    connectedCallback() {
      if (this.connectControls()) this.update(this.controls.selected, false);
    }
    connectControls() {
      const info = Array.from(document.querySelectorAll('product-info[data-sc-shared-view]')).find((node) => node.dataset.productId === this.dataset.productId);
      const controls = info?.querySelector('dwan-sc-shared-controls');
      if (this.initialized && this.info === info && this.controls === controls && controls?.initialized) return true;
      this.removeEventListener('click', this.onClick); this.initialized = false;
      this.info = null; this.controls = null; this.selected = null;
      if (!controls?.initialized || typeof controls.integrity !== 'boolean') return false;
      this.info = info; this.controls = controls; this.initialized = true;
      this.profile = controls.profile; this.native = controls.native; this.hbOptionRoles = roles(this.profile, this.native);
      this.onClick = this.handleClick.bind(this); this.addEventListener('click', this.onClick);
      this.querySelectorAll('[data-hb-load-video]').forEach((button) => { button.hidden = !embedUrl(button.dataset.videoUrl); });
      return true;
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick); this.initialized = false;
      this.info = null; this.controls = null; this.selected = null;
      this.clearSelection();
    }
    setFeedback(message) {
      this.querySelectorAll('[data-hb-details-feedback]').forEach((node) => { node.textContent = message || ''; node.hidden = !message; });
      this.setText('[data-hb-details-status]', message || '');
    }
    setText(selector, value) { this.querySelectorAll(selector).forEach((node) => { node.textContent = value || ''; }); }
    setModel(value) {
      this.setText('[data-hb-summary-model]', value);
      this.querySelectorAll('[data-hb-card-model-row]').forEach((row) => { row.hidden = !value; });
    }
    clearSelection() {
      this.selected = null; this.setModel('');
      this.querySelectorAll('[data-hb-coverage-choice]').forEach((link) => { link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true'); link.removeAttribute('aria-current'); });
      this.querySelectorAll('[data-hb-coverage-card]').forEach((card) => { card.dataset.selected = 'false'; });
    }
    updateImage(figure, image) {
      if (!figure) return; figure.hidden = !image?.src;
      const img = figure.querySelector('img'); if (!img || !image?.src) return;
      img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = image.src; img.alt = image.alt || '';
      if (image.width && image.height) { img.width = image.width; img.height = image.height; }
    }
    update(selected, announce) {
      if (!this.connectControls()) { this.clearSelection(); this.setFeedback('Checking your selected model and coverage…'); return; }
      this.profile = this.controls.profile; this.native = this.controls.native; this.hbOptionRoles = roles(this.profile, this.native);
      if (!this.hbOptionRoles?.valid || !this.controls.integrity) {
        this.clearSelection();
        this.setFeedback('Your selection could not be verified. Please reselect your model and coverage above before ordering.');
        this.querySelectorAll('[data-hb-coverage-choice]').forEach((link) => {link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true');});
        this.querySelectorAll('[data-hb-summary-materials], [data-hb-card-materials]').forEach((node) => node.replaceChildren());
        this.querySelectorAll('[data-hb-variant-figure]').forEach((node) => {node.hidden = true;});
        this.setText('[data-hb-summary-model], [data-hb-summary-coverage], [data-hb-summary-price], [data-hb-summary-pieces]', '');
        return;
      }
      const current = !this.controls.selectionPending && this.controls.hasVerifiedNativeResponse() ? this.controls.readVariant() : null;
      selected = current && String(current.id) === String(this.controls.selected?.id) && String(current.id) === String(selected?.id) ? this.native.variants.find((variant) => String(variant.id) === String(current.id)) : null;
      this.selected = selected;
      const model = selected?.options[this.hbOptionRoles.model];
      const selectionValues = selected?.options || this.controls.readSelectionValues?.();
      this.setFeedback(this.controls.selectionPending ? 'Updating your selected model and coverage…' : selected ? '' : 'Choose an available model and coverage above before ordering.');
      this.querySelectorAll('[data-hb-coverage-card]').forEach((card) => {
        const coverage = optionValueById(this.native, this.hbOptionRoles.coverage, card.dataset.hbCoverageId)?.value;
        const match = selectionValues ? this.native.variants.find((variant) => variant.options.every((value, index) => value === (index === this.hbOptionRoles.coverage ? coverage : selectionValues[index]))) : null;
        const current = Boolean(match && selected && String(match.id) === String(selected.id));
        card.dataset.selected = String(current);
        const choice = card.querySelector('[data-hb-coverage-choice]');
        if (choice) {
          choice.setAttribute('aria-disabled', String(!match?.available));
          if (match?.available) choice.href = withViewContext(this.native.url, { variant: match.id }); else choice.removeAttribute('href');
          if (current) choice.setAttribute('aria-current', 'true'); else choice.removeAttribute('aria-current');
        }
        card.querySelectorAll('[data-hb-choice-label]').forEach((label) => { label.textContent = current ? 'Selected' : match?.available ? 'Choose coverage' : 'Unavailable for this selection'; });
        const heading = card.querySelector('[data-hb-coverage-title]'); if (heading) heading.textContent = coverageLabel(this.profile, coverage, model);
        const price = card.querySelector('[data-hb-card-price]'); if (price) price.textContent = match?.price || '';
        this.updateImage(card.querySelector('.hb-details__coverage-image'), match?.image);
        renderMaterials(card.querySelector('[data-hb-card-materials]'), this.profile, coverage, model);
      });
      const coverage = selected?.options[this.hbOptionRoles.coverage];
      this.setModel(selectionValues?.[this.hbOptionRoles.model]);
      this.setText('[data-hb-summary-coverage]', coverageLabel(this.profile, coverage, model));
      this.setText('[data-hb-summary-price]', selected?.price);
      const definition = packageDefinition(this.profile, coverage);
      this.setText('[data-hb-summary-pieces]', definition ? definition.included.map((code) => `${code} · ${surfaceLabel(this.profile, code, model)}`).join(' + ') : '');
      this.querySelectorAll('[data-hb-summary-materials]').forEach((node) => renderMaterials(node, this.profile, coverage, model));
      this.querySelectorAll('[data-hb-variant-figure]').forEach((figure) => this.updateImage(figure, selected?.image));
      this.querySelectorAll('[data-hb-review]').forEach((link) => { link.href = `#${this.info.id}`; });
      if (announce) this.setText('[data-hb-details-status]', selected ? `${selected.options[this.hbOptionRoles.model]}. ${coverageLabel(this.profile, coverage, model)}. ${selected.price}.` : 'This combination is unavailable.');
    }
    handleClick(event) {
      const video = event.target.closest('[data-hb-load-video]');
      if (video && this.contains(video)) {
        const src = embedUrl(video.dataset.videoUrl); if (!src) return;
        const frame = document.createElement('iframe'); frame.src = src; frame.title = video.dataset.videoTitle || 'Product demonstration'; frame.width = '560'; frame.height = '315';
        frame.setAttribute('allow', 'encrypted-media; picture-in-picture'); frame.setAttribute('allowfullscreen', ''); frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        video.parentElement.replaceChildren(frame); frame.focus(); return;
      }
      const picker = this.info?.querySelector('variant-selects');
      const review = event.target.closest('[data-hb-review]');
      if (review && this.contains(review) && picker) { event.preventDefault(); picker.scrollIntoView({ block: 'start', behavior: 'auto' }); picker.querySelector('select, input:not([type="hidden"])')?.focus({ preventScroll: true }); return; }
      const choice = event.target.closest('[data-hb-coverage-choice]');
      if (!choice || !this.contains(choice)) return;
      if (choice.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
      const values = this.selected?.options || this.controls.readSelectionValues?.();
      if (!picker || !values || !this.hbOptionRoles.valid) return;
      const target = optionValueById(this.native, this.hbOptionRoles.coverage, choice.dataset.hbCoverageId);
      if (!target) { event.preventDefault(); return; }
      const match = this.native.variants.find((variant) => variant.options.every((value, index) => value === (index === this.hbOptionRoles.coverage ? target.value : values[index])));
      if (!match?.available) { event.preventDefault(); return; }
      const groups = Array.from(picker.querySelectorAll('.product-form__input'));
      const group = groups[this.hbOptionRoles.coverage];
      const select = group?.querySelector('select');
      const option = select && Array.from(select.options).find((value) => String(value.dataset.optionValueId) === String(target.id));
      if (select && option) {
        event.preventDefault(); if (String(select.selectedOptions?.[0]?.dataset.optionValueId) === String(target.id)) return;
        select.selectedIndex = Array.from(select.options).indexOf(option); select.dispatchEvent(new Event('change', { bubbles: true })); return;
      }
      const radio = group && Array.from(group.querySelectorAll('input[type="radio"]')).find((input) => String(input.dataset.optionValueId) === String(target.id));
      if (radio) { event.preventDefault(); if (radio.checked) return; radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      // If a native control is unavailable, the real variant URL remains a safe fallback.
    }
  });

  if (!customElements.get('dwan-sc-shared-review')) customElements.define('dwan-sc-shared-review', class extends HTMLElement {
    connectedCallback() {
      if (this.hbStickyConnected) return;
      this.info = this.closest('product-info'); this.form = this.closest('form');
      if (!this.info || !this.form) return;
      this.hbStickyConnected = true;
      this.button = this.querySelector('[data-hb-sticky-add]');
      this.actions = this.querySelector('[data-hb-sticky-actions]');
      this.payment = this.querySelector('[data-hb-sticky-payment]');
      this.placeholder = this.form.querySelector('[data-hb-sticky-placeholder]');
      this.inlineHeight = 0; this.fixedSafe = null;
      this.refreshFrame = undefined;
      this.queueRefresh = (event) => {
        if (event?.type === 'resize') this.fixedSafe = null;
        if (this.refreshFrame !== undefined) return;
        this.refreshFrame = window.requestAnimationFrame(() => {
          this.refreshFrame = undefined;
          this.refresh();
        });
      };
      this.onInput = (event) => { if (event.target?.matches('input[name="quantity"]')) this.queueRefresh(); };
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = (event) => { if (!this.allowActivation()) { event.preventDefault(); event.stopImmediatePropagation(); } };
      this.addEventListener('click', this.onClick, true);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.info.addEventListener('input', this.onInput);
      this.info.addEventListener('change', this.onInput);
      window.addEventListener('scroll', this.queueRefresh, { passive: true });
      window.addEventListener('resize', this.queueRefresh);
      window.visualViewport?.addEventListener('resize', this.queueRefresh);
      this.footer = document.querySelector('footer.footer, footer');
      this.sentinel = this.info.querySelector('[data-hb-sticky-sentinel]');
      if (typeof IntersectionObserver !== 'undefined') {
        this.viewportObserver = new IntersectionObserver(this.queueRefresh);
        if (this.sentinel) this.viewportObserver.observe(this.sentinel);
        if (this.footer) this.viewportObserver.observe(this.footer);
      }
      if (typeof ResizeObserver !== 'undefined') {
        this.sizeObserver = new ResizeObserver(this.queueRefresh); this.sizeObserver.observe(this);
      }
      this.domObserver = new MutationObserver((records) => {
        if (records.some(({ target }) => {
          const nativeAction = this.actions?.contains(target);
          if (this.contains(target) && !nativeAction) return false;
          const ancestor = target.contains?.(this) && target !== this;
          if (ancestor) this.fixedSafe = null;
          return nativeAction || this.info?.contains(target) || ancestor || target === document.body || target.closest?.('cart-drawer, cart-notification, modal-dialog, product-modal, quick-add-modal, menu-drawer, details.menu-drawer-container, dialog');
        })) this.queueRefresh();
      });
      this.domObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'disabled', 'aria-disabled', 'aria-busy', 'inert', 'value', 'min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'] });
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        ['optionValueSelectionChange', 'variantChange', 'quantityUpdate', 'cartUpdate', 'cartError'].forEach((name) => {
          if (PUB_SUB_EVENTS[name]) this.unsubscribers.push(subscribe(PUB_SUB_EVENTS[name], () => this.queueRefresh()));
        });
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.viewportObserver?.disconnect(); this.domObserver?.disconnect(); this.sizeObserver?.disconnect();
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe?.());
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = undefined;
      this.removeEventListener('click', this.onClick, true);
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.info?.removeEventListener('input', this.onInput); this.info?.removeEventListener('change', this.onInput);
      window.removeEventListener('scroll', this.queueRefresh); window.removeEventListener('resize', this.queueRefresh);
      window.visualViewport?.removeEventListener('resize', this.queueRefresh);
      delete this.dataset.hbDocked;
      this.hidden = false;
      if (this.placeholder) this.placeholder.hidden = true;
      if (this.payment) { this.payment.hidden = true; this.payment.inert = true; }
      if (this.actions) this.actions.inert = true;
      this.hbStickyConnected = false; this.restoreFocus = false;
      this.info = null; this.form = null; this.button = null; this.sentinel = null; this.lastCart = null;
    }
    readState() {
      const info = this.info;
      const controls = info?.querySelector('dwan-sc-shared-controls');
      const productForm = info?.querySelector('product-form');
      const form = productForm?.querySelector('form');
      const submit = productForm?.querySelector('button[type="submit"][name="add"]');
      const quantity = info?.querySelector('input[name="quantity"]');
      const guard = info?.querySelector('[data-hb-purchase-guard]');
      const hbOptionRoles = roles(controls?.profile, controls?.native);
      const current = controls?.integrity ? controls.readVariant?.() : null;
      const selected = current && controls?.native?.variants?.find((variant) => String(variant.id) === String(current.id));
      const rawQuantity = String(quantity?.value || '').trim();
      const amount = Number(rawQuantity);
      const min = Math.max(1, Number(quantity?.min || 1));
      const max = quantity?.max ? Number(quantity.max) : Infinity;
      const step = Number(quantity?.step || 1);
      const rawRuleMin = String(quantity?.dataset?.min ?? '').trim();
      const rawCartQuantity = String(quantity?.dataset?.cartQuantity ?? '').trim();
      const rawRuleMax = String(quantity?.dataset?.max ?? '').trim();
      const ruleMin = Number(rawRuleMin); const cartQuantity = Number(rawCartQuantity);
      const ruleMax = rawRuleMax ? Number(rawRuleMax) : Infinity;
      const combinedQuantity = cartQuantity + amount;
      const quantityRuleValid = Boolean(rawRuleMin && rawCartQuantity && Number.isSafeInteger(ruleMin) && ruleMin >= 1 && Number.isSafeInteger(cartQuantity) && cartQuantity >= 0 && (!rawRuleMax || Number.isSafeInteger(ruleMax) && ruleMax >= ruleMin) && Number.isSafeInteger(combinedQuantity) && combinedQuantity >= ruleMin && combinedQuantity <= ruleMax && Number.isSafeInteger(step) && step > 0 && combinedQuantity % step === 0);
      const quantityValid = Boolean(quantityRuleValid && rawQuantity && Number.isSafeInteger(amount) && amount >= min && amount <= max && Number.isSafeInteger(step) && step > 0 && (amount - min) % step === 0 && quantity?.validity?.valid !== false && !quantity?.disabled && quantity?.form === form);
      let payloadValid = false;
      if (form && typeof FormData === 'function') {
        try {
          const payload = new FormData(form);
          const ids = payload.getAll('id');
          const quantities = payload.getAll('quantity');
          payloadValid = ids.length === 1 && String(ids[0]) === String(selected?.id) && quantities.length === 1 && String(quantities[0]).trim() !== '' && Number(quantities[0]) === amount;
        } catch { payloadValid = false; }
      }
      let amountText = null;
      const cents = current?.price;
      const total = cents * amount;
      const currency = this.dataset.currency;
      if (current && typeof cents === 'number' && Number.isSafeInteger(cents) && cents >= 0 && Number.isSafeInteger(total) && total >= 0 && /^[A-Z]{3}$/.test(currency || '')) {
        try {
          const locale = document.documentElement?.lang || 'en';
          if (!this.priceFormatter || this.priceFormatter.locale !== locale || this.priceFormatter.currency !== currency) {
            this.priceFormatter = { locale, currency, formatter: new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'symbol' }) };
          }
          const formatter = this.priceFormatter.formatter;
          amountText = this.dataset.priceMode === 'unit' ? selected?.price : `${formatter.format(total / 100)} ${currency}`;
        } catch { amountText = null; }
      }
      const pending = Boolean(controls?.selectionPending || info?.pendingRequestUrl);
      const busy = Boolean(submit?.classList.contains('loading'));
      const nativeDisabled = submit?.getAttribute('aria-disabled') === 'true';
      const nativeResponseVerified = !controls?.selectionState || controls.hasVerifiedNativeResponse?.() === true;
      const valid = Boolean(nativeResponseVerified && info?.isConnected !== false && this.closest('product-info') === info && info?.contains(this) && controls?.initialized && controls.integrity && hbOptionRoles.valid && !pending && current?.available === true && selected?.available === true && String(selected.id) === String(controls.selected?.id) && productForm?.form === form && typeof productForm?.onSubmitHandler === 'function' && this.closest('form') === form && form === this.form && form?.getAttribute('id') === this.dataset.formId && submit?.form === form && String(form?.querySelector('input[name="id"]')?.value || '') === String(selected.id) && guard?.contains(productForm) && !guard.disabled && !guard.inert && !submit?.disabled && !submit?.matches(':disabled') && !busy && !nativeDisabled && quantityValid && payloadValid && amountText !== null);
      return { controls, productForm, form, submit, quantity, quantityValid, payloadValid, amount, hbOptionRoles, selected, current, amountText, pending, busy, nativeDisabled, nativeResponseVerified, valid };
    }
    overlayIsVisible(node) {
      if (!node || node.hidden) return false;
      if (typeof window.getComputedStyle !== 'function') return true;
      for (let current = node; current; current = current.parentElement) {
        const style = window.getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity !== '' && style.opacity != null && Number(style.opacity) <= 0) return false;
      }
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
    }
    blockedByOverlay() {
      const body = document.body;
      const lockClass = ['overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet'].some((name) => body.classList.contains(name));
      const bodyStyle = typeof window.getComputedStyle === 'function' ? window.getComputedStyle(body) : null;
      const lock = lockClass && (!bodyStyle || ['hidden', 'clip'].includes(bodyStyle.overflowY));
      return lock || Array.from(document.querySelectorAll('cart-drawer.active, #cart-notification.active, modal-dialog[open], product-modal[open], quick-add-modal[open], dialog[open], details.menu-drawer-container[open]')).some((node) => this.overlayIsVisible(node));
    }
    setText(selector, value) {
      const node = this.querySelector(selector);
      const text = value == null ? '' : String(value);
      if (node && node.textContent !== text) node.textContent = text;
    }
    canUseViewportFixed() {
      if (typeof window.getComputedStyle !== 'function') return false;
      for (let node = this.parentElement; node; node = node.parentElement) {
        const style = window.getComputedStyle(node);
        if ([style.transform, style.perspective, style.filter, style.backdropFilter].some((value) => value && value !== 'none') || /transform|perspective|filter|contain/.test(style.willChange || '') || /layout|paint|strict|content/.test(style.contain || '') || style.contentVisibility === 'auto') return false;
        if (node !== document.body && node !== document.documentElement && [style.overflow, style.overflowX, style.overflowY].some((value) => value === 'hidden' || value === 'clip')) return false;
      }
      return true;
    }
    refresh() {
      if (!this.hbStickyConnected || !this.button) return;
      const state = this.readState();
      const verified = state.hbOptionRoles.valid && state.controls?.integrity && !state.pending && state.selected && String(state.selected.id) === String(state.controls?.selected?.id);
      this.lastCart = state.productForm?.cart;
      this.setText('[data-hb-sticky-product]', `${state.controls?.profile?.product_title || ''}${state.controls?.profile?.colour_label ? ` · ${state.controls.profile.colour_label}` : ''}`);
      this.setText('[data-hb-sticky-model]', verified ? state.selected.options[state.hbOptionRoles.model] : '');
      this.setText('[data-hb-sticky-coverage]', verified ? coverageLabel(state.controls.profile, state.selected.options[state.hbOptionRoles.coverage], state.selected.options[state.hbOptionRoles.model]) : '');
      this.setText('[data-hb-sticky-price-label]', this.dataset.priceMode === 'unit' ? this.dataset.unitLabel : this.dataset.totalLabel);
      this.setText('[data-hb-sticky-price]', verified && state.quantityValid ? state.amountText : '');
      this.setText('[data-hb-sticky-quantity]', state.quantityValid ? state.amount : '—');
      if (this.actions) {
        if (this.actions.inert !== !state.valid) this.actions.inert = !state.valid;
        const value = String(!state.valid);
        if (this.actions.getAttribute('aria-disabled') !== value) this.actions.setAttribute('aria-disabled', value);
      }
      if (this.payment) {
        if (this.payment.hidden !== !state.valid) this.payment.hidden = !state.valid;
        if (this.payment.inert !== !state.valid) this.payment.inert = !state.valid;
      }
      const status = this.querySelector('[data-hb-sticky-status]');
      const nativeStatus = this.info.querySelector('[data-hb-integrity-status]');
      this.setText('[data-hb-sticky-status]', !verified || !state.nativeResponseVerified ? nativeStatus?.textContent || '' : !state.quantityValid ? this.dataset.quantityErrorLabel : !state.payloadValid || state.amountText === null ? this.dataset.selectionErrorLabel : !state.busy && (state.submit?.disabled || state.nativeDisabled) ? state.submit?.querySelector('span')?.textContent || this.dataset.selectionErrorLabel : !state.valid && !state.busy ? this.dataset.selectionErrorLabel : '');
      if (status && status.hidden !== !status.textContent) status.hidden = !status.textContent;
      const error = this.querySelector('[data-hb-sticky-error]');
      const wrapper = state.productForm?.querySelector('.product-form__error-message-wrapper');
      this.setText('[data-hb-sticky-error]', wrapper && !wrapper.hidden ? wrapper.querySelector('.product-form__error-message')?.textContent : '');
      if (error && error.hidden !== !error.textContent) error.hidden = !error.textContent;
      const blocked = this.blockedByOverlay();
      if (blocked && (this.contains(document.activeElement) || this.lastCart?.activeElement === this.button)) this.restoreFocus = true;
      this.sentinel = this.info.querySelector('[data-hb-sticky-sentinel]');
      const past = Boolean(this.sentinel && this.sentinel.getBoundingClientRect().bottom <= 0);
      const wasDocked = this.dataset.hbDocked === 'true';
      if (!wasDocked) {
        this.hidden = false;
        const measured = Math.ceil(this.getBoundingClientRect().height);
        if (Number.isFinite(measured) && measured > 0) this.inlineHeight = measured;
      }
      if (this.fixedSafe === null || past && !this.fixedSafe) this.fixedSafe = this.canUseViewportFixed();
      const docked = Boolean(past && this.inlineHeight > 0 && this.placeholder && this.fixedSafe);
      const footer = this.footer?.getBoundingClientRect();
      const footerVisible = Boolean(footer && footer.top < window.innerHeight && footer.bottom > 0);
      if (docked) {
        const height = `${this.inlineHeight}px`;
        if (this.placeholder.style.height !== height) this.placeholder.style.height = height;
        if (this.placeholder.hidden) this.placeholder.hidden = false;
        if (this.dataset.hbDocked !== 'true') this.dataset.hbDocked = 'true';
        this.hidden = blocked || footerVisible;
      } else {
        delete this.dataset.hbDocked; this.hidden = false;
        if (this.placeholder && !this.placeholder.hidden) this.placeholder.hidden = true;
      }
      this.dataset.hbFixedUnavailable = String(past && !this.fixedSafe);
      const summary = this.querySelector('[data-hb-sticky-summary]');
      if (summary && summary.hidden !== !docked) summary.hidden = !docked;
      if (!blocked && this.restoreFocus) {
        const active = document.activeElement;
        if (!this.hidden && this.lastCart?.activeElement === this.button && (active === document.body || active === this.button || active?.closest('cart-drawer, cart-notification'))) this.button.focus({ preventScroll: true });
        this.restoreFocus = false;
      }
    }
    allowActivation() {
      const state = this.readState();
      if (!state.valid || this.hidden || this.blockedByOverlay()) {
        this.refresh();
        if (!state.quantityValid) {
          state.quantity?.scrollIntoView({ block: 'center', behavior: 'auto' }); state.quantity?.focus({ preventScroll: true }); state.quantity?.reportValidity?.();
        }
        return false;
      }
      return typeof state.form.reportValidity !== 'function' || state.form.reportValidity();
    }
    handleClick(event) {
      const target = event.target;
      const action = target?.closest?.('[data-hb-sticky-add], [data-hb-sticky-payment]');
      if (!action || !this.contains(action)) return;
      if (!this.allowActivation()) { event.preventDefault(); event.stopImmediatePropagation(); }
    }
  });
})();

